import React, { useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { motion } from 'framer-motion';

const contactDetails = [
  {
    title: 'Phone',
    bg: 'bg-blue-50',
    icon: (
      <svg className="w-5 h-5 text-blueblack" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    lines: [
      { label: '+233 54 496 6829', href: 'tel:+233544966829' },
      { label: '+233 24 727 9067', href: 'tel:+233247279067' },
      { label: '+233 26 417 5879', href: 'tel:+233264175879' }
    ],
  },
  {
    title: 'Email',
    bg: 'bg-yellow-50',
    icon: (
      <svg className="w-5 h-5 text-blueblack" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    lines: [
      { label: 'info@eitghana.com', href: 'mailto:info@eitghana.com' },
    ],
  },
  {
    title: 'Follow Us',
    bg: 'bg-purple-50',
    icon: (
      <svg className="w-5 h-5 text-blueblack" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    lines: [
      { label: 'LinkedIn',  href: 'https://www.linkedin.com/company/eit-ghana-limited', external: true },
      { label: 'Facebook',  href: 'https://web.facebook.com/eitghanalimited',           external: true },
      { label: 'Instagram', href: 'https://www.instagram.com/eitghanalimited',          external: true },
    ],
  },
];

const inputClass =
  'border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-seablue focus:border-transparent transition-all w-full';

export default function Contact() {
  const form = useRef();

  const sendEmail = (e) => {
    e.preventDefault();
    emailjs
      .sendForm('service_ns9p1r8', 'template_bnndmof', form.current, '9_xhzH6d0qL8_WlYq')
      .then(
        (result) => console.log(result.text),
        (error)  => console.log(error.text)
      );
    e.target.reset();
  };

  useEffect(() => {
    document.title = 'EIT Ghana Limited - Contact Us';
  }, []);

  return (
    <motion.div
      className="pt-16 bg-gray-50 min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >

      {/* ── BANNER CARD ── */}
      <section className="pt-8 pb-0">
        <div className="max-w-7xl mx-auto px-5 lg:px-20">
          <motion.div
            className="bg-blueblack rounded-3xl p-10 lg:p-16 relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
          >
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-yellowcolor/10 rounded-full" />
            <div className="absolute -bottom-10 right-32 w-36 h-36 bg-seablue/20 rounded-full" />
            <div className="relative z-10 max-w-xl">
              <p className="text-yellowcolor font-semibold text-xs uppercase tracking-widest mb-4">Get In Touch</p>
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">Contact Us</h1>
              <p className="text-gray-300 text-lg leading-relaxed">
                The only innovative hub you'll ever need. We'd love to hear from you.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CONTACT BENTO ── */}
      <section className="py-8 pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-20">
          <div className="grid lg:grid-cols-5 gap-5">

            {/* Left: contact info cards */}
            <motion.div
              className="lg:col-span-2 flex flex-col gap-4"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              {/* Intro card */}
              <div className="bg-white rounded-3xl p-6 shadow-sm">
                <h2 className="text-xl font-bold text-blueblack mb-2">Let's Talk</h2>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Fill out the form and we'll get back to you as soon as possible.
                </p>
              </div>

              {/* Info cards */}
              {contactDetails.map(({ title, bg, icon, lines }) => (
                <div key={title} className="bg-white rounded-2xl p-5 shadow-sm flex gap-4">
                  <div className={`w-10 h-10 flex-shrink-0 ${bg} rounded-xl flex items-center justify-center`}>
                    {icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-blueblack text-sm mb-1">{title}</h3>
                    {lines.map(({ label, href, external }) => (
                      <a
                        key={label}
                        href={href}
                        target={external ? '_blank' : undefined}
                        rel={external ? 'noreferrer' : undefined}
                        className="block text-gray-500 hover:text-seablue text-sm transition-colors leading-relaxed"
                      >
                        {label}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Right: form card */}
            <motion.div
              className="lg:col-span-3 bg-white rounded-3xl p-8 lg:p-10 shadow-sm"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold text-blueblack mb-6">Send Us a Message</h3>
              <form ref={form} onSubmit={sendEmail} className="grid gap-4">
                <div className="grid gap-1.5">
                  <label className="text-sm font-semibold text-gray-700" htmlFor="user_name">Full Name</label>
                  <input className={inputClass} type="text" placeholder="Your full name" name="user_name" id="user_name" required />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-1.5">
                    <label className="text-sm font-semibold text-gray-700" htmlFor="user_email">Email Address</label>
                    <input className={inputClass} type="email" placeholder="your@email.com" name="user_email" id="user_email" required />
                  </div>
                  <div className="grid gap-1.5">
                    <label className="text-sm font-semibold text-gray-700" htmlFor="tel">Phone Number</label>
                    <input className={inputClass} type="tel" placeholder="+233 00 000 0000" name="tel" id="tel" required />
                  </div>
                </div>

                <div className="grid gap-1.5">
                  <label className="text-sm font-semibold text-gray-700" htmlFor="subject">Subject</label>
                  <input className={inputClass} type="text" placeholder="How can we help?" name="subject" id="subject" required />
                </div>

                <div className="grid gap-1.5">
                  <label className="text-sm font-semibold text-gray-700" htmlFor="message">Message</label>
                  <textarea
                    className={`${inputClass} resize-none`}
                    name="message"
                    rows="5"
                    placeholder="Tell us more about your inquiry..."
                    id="message"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-blueblack text-white font-semibold rounded-full hover:bg-seablue transition-all duration-300 hover:shadow-lg mt-1"
                >
                  Send Message
                </button>
              </form>
            </motion.div>

          </div>
        </div>
      </section>

    </motion.div>
  );
}
